<template>
  <div class="customer-transaction-stages-input">
    <div
      v-for="(stage, index) in stages"
      :key="`stage-${index}`"
      class="customer-transaction-stages-input__row"
    >
      <span class="customer-transaction-stages-input__label">阶段{{ index + 1 }}</span>
      <el-input-number
        :value="stage"
        :min="1"
        :max="100"
        :precision="0"
        :controls="false"
        class="customer-transaction-stages-input__number"
        @change="handleStageChange(index, $event)"
      />
      <span class="customer-transaction-stages-input__suffix">%</span>
      <el-button
        plain
        type="danger"
        size="mini"
        icon="el-icon-delete"
        :disabled="stages.length === 1"
        @click="handleRemove(index)"
      />
    </div>

    <div class="customer-transaction-stages-input__footer">
      <el-button
        plain
        type="primary"
        size="mini"
        icon="el-icon-plus"
        @click="handleAdd"
      >
        添加阶段
      </el-button>
      <span
        class="customer-transaction-stages-input__summary"
        :class="{ 'is-error': total !== 100 }"
      >
        总计 {{ total }}%
      </span>
    </div>
  </div>
</template>

<script>
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';

@Component
export default class CustomerTransactionStagesInput extends Vue {
  @Prop({ type: Array, default: () => [100] }) value;

  stages = [100];

  get total() {
    return this.stages.reduce((sum, stage) => sum + stage, 0);
  }

  sanitizeStage(value, fallback = 1) {
    const parsed = Number.parseInt(value, 10);

    if (!Number.isFinite(parsed)) {
      return fallback;
    }

    return Math.max(1, Math.min(100, parsed));
  }

  normalizeStages(value) {
    if (!Array.isArray(value) || !value.length) {
      return [100];
    }

    return value.map(stage => this.sanitizeStage(stage));
  }

  emitStages(stages) {
    const nextStages = this.normalizeStages(stages);

    this.stages = nextStages;
    this.$emit('input', nextStages);
    this.$emit('change', nextStages);
  }

  getAdjustIndex(index) {
    return index === this.stages.length - 1 ? index - 1 : this.stages.length - 1;
  }

  findSplitIndex() {
    let splitIndex = -1;
    let maxStage = 1;

    this.stages.forEach((stage, index) => {
      if (stage > maxStage) {
        maxStage = stage;
        splitIndex = index;
      }
    });

    return splitIndex;
  }

  handleStageChange(index, value) {
    if (this.stages.length === 1) {
      this.emitStages([100]);
      return;
    }

    const nextStages = [...this.stages];
    const oldValue = nextStages[index];
    const adjustIndex = this.getAdjustIndex(index);
    const adjustValue = nextStages[adjustIndex];
    const sanitizedValue = this.sanitizeStage(value, oldValue);
    const minValue = Math.max(1, oldValue - (100 - adjustValue));
    const maxValue = oldValue + adjustValue - 1;
    const boundedValue = Math.max(minValue, Math.min(maxValue, sanitizedValue));

    nextStages[index] = boundedValue;
    nextStages[adjustIndex] = adjustValue - (boundedValue - oldValue);

    this.emitStages(nextStages);
  }

  handleAdd() {
    const splitIndex = this.findSplitIndex();

    if (splitIndex === -1) {
      this.$message.warning('当前阶段比例无法继续拆分');
      return;
    }

    const nextStages = [...this.stages];
    const currentStage = nextStages[splitIndex];
    const firstStage = Math.ceil(currentStage / 2);
    const secondStage = currentStage - firstStage;

    nextStages.splice(splitIndex, 1, firstStage, secondStage);
    this.emitStages(nextStages);
  }

  handleRemove(index) {
    if (this.stages.length === 1) {
      return;
    }

    const nextStages = [...this.stages];
    const removedStage = nextStages.splice(index, 1)[0];
    const mergeIndex = index > 0 ? index - 1 : 0;

    nextStages[mergeIndex] += removedStage;
    this.emitStages(nextStages);
  }

  @Watch('value', { immediate: true, deep: true })
  onValueChange(value) {
    this.stages = this.normalizeStages(value);
  }
}
</script>

<style scoped>
.customer-transaction-stages-input {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.customer-transaction-stages-input__row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.customer-transaction-stages-input__label {
  min-width: 56px;
  color: #606266;
}

.customer-transaction-stages-input__number {
  width: 120px;
}

.customer-transaction-stages-input__suffix {
  color: #909399;
}

.customer-transaction-stages-input__footer {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.customer-transaction-stages-input__summary {
  color: #67c23a;
  font-size: 13px;
}

.customer-transaction-stages-input__summary.is-error {
  color: #f56c6c;
}
</style>
